import React from "react";
import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useGlobalUtils } from "../../../lib/globalUtils";

const AiAgentsDistributionChart = ({ data, isLoading, error }) => {
  const { t } = useTranslation("overview");
  const { parsePrice } = useGlobalUtils();

  // Define colors for different providers
  const COLORS = {
    whatsapp: "#10B981", // Green
    ordering: "#3B82F6", // Blue
    phone: "#8B5CF6", // Purple
    sms: "#F59E0B", // Orange
    web_chat: "#3B82F6", // Blue
    default: "#6B7280", // Gray
  };

  // Transform data for the chart
  const chartData =
    data?.orders?.map((agent) => {
      // Create display name based on provider
      let displayName = "";
      if (agent.provider === "whatsapp") {
        displayName = t("agentsDistribution.providers.whatsapp");
      } else if (agent.provider === "ordering") {
        displayName = t("agentsDistribution.providers.ordering");
      } else {
        const normalizedProvider = agent.provider
          ? `${agent.provider.charAt(0).toUpperCase()}${agent.provider.slice(1)}`
          : "-";
        displayName = t("agentsDistribution.providers.default", {
          provider: normalizedProvider,
        });
      }

      return {
        name: displayName,
        value: agent.orders,
        percentage: agent.participation_percentage,
        revenue: agent.revenue,
        provider: agent.provider,
        provider_type: agent.provider_type,
        color: COLORS[agent.provider] || COLORS.default,
      };
    }) || [];

  // Calculate total orders
  const totalOrders = chartData.reduce((sum, item) => sum + item.value, 0);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="relative z-50 rounded-lg border border-gray-700 bg-gray-800 p-2 shadow-xl sm:p-3">
          <p className="text-xs font-medium text-white sm:text-sm">
            {data.name}
          </p>
          <p className="text-xs text-gray-300">
            {t("agentsDistribution.tooltip.orders")}:{" "}
            <span className="font-medium text-white">{data.value}</span>
          </p>
          <p className="text-xs text-gray-300">
            {t("agentsDistribution.tooltip.percentage")}:{" "}
            <span className="font-medium text-white">
              {data?.percentage?.toFixed(2)}%
            </span>
          </p>
          <p className="text-xs text-gray-300">
            {t("agentsDistribution.tooltip.revenue")}:{" "}
            <span className="font-medium text-white">
              {parsePrice(data.revenue ?? 0)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Get icon for provider
  const getProviderIcon = (provider) => {
    switch (provider) {
      case "whatsapp":
        return (
          <svg
            className="h-3 w-3 sm:h-4 sm:w-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
          </svg>
        );
      case "ordering":
        return (
          <svg
            className="h-3 w-3 sm:h-4 sm:w-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>
        );
      case "phone":
        return (
          <svg
            className="h-3 w-3 sm:h-4 sm:w-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
          </svg>
        );
      default:
        return (
          <svg
            className="h-3 w-3 sm:h-4 sm:w-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        );
    }
  };

  // Custom legend
  const CustomLegend = ({ payload }) => {
    return (
      <div className="mt-3 flex flex-col space-y-2 sm:mt-4 sm:space-y-3">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center space-x-2 sm:space-x-3">
            <div
              className="h-2 w-2 flex-shrink-0 rounded-full sm:h-3 sm:w-3"
              style={{ backgroundColor: entry.color }}
            />
            <div className="flex flex-shrink-0 items-center space-x-1 text-gray-300 sm:space-x-2">
              {getProviderIcon(entry.provider)}
            </div>
            <div className="flex min-w-0 flex-1 items-center space-x-1 sm:space-x-2">
              <span className="truncate text-xs font-medium text-white sm:text-sm">
                {entry.name}:
              </span>
              <span className="flex-shrink-0 text-xs text-gray-300 sm:text-sm">
                {entry.percentage?.toFixed(1)}%
              </span>
              <span className="hidden flex-shrink-0 text-xs text-gray-400 sm:inline sm:text-sm">
                {t("agentsDistribution.legend.ordersCount", {
                  count: entry.value,
                })}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-3 sm:p-6">
        <div className="animate-pulse">
          <div className="mb-3 h-5 w-1/3 rounded bg-gray-700 sm:mb-4 sm:h-6"></div>
          <div className="mb-4 h-3 w-1/2 rounded bg-gray-700 sm:mb-6 sm:h-4"></div>
          <div className="flex h-48 items-center justify-center sm:h-64">
            <div className="h-24 w-24 rounded-full bg-gray-700 sm:h-32 sm:w-32"></div>
          </div>
          <div className="mt-3 space-y-2 sm:mt-4">
            <div className="h-3 w-3/4 rounded bg-gray-700 sm:h-4"></div>
            <div className="h-3 w-1/2 rounded bg-gray-700 sm:h-4"></div>
            <div className="h-3 w-2/3 rounded bg-gray-700 sm:h-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-3 sm:p-6">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 sm:mb-4 sm:h-16 sm:w-16">
            <svg
              className="h-6 w-6 text-red-400 sm:h-8 sm:w-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-base font-medium text-red-400 sm:text-lg">
            {t("agentsDistribution.error.title")}
          </h3>
          <p className="text-sm text-gray-400">
            {error.message || t("agentsDistribution.error.subtitle")}
          </p>
        </div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-3 sm:p-6">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-500/10 sm:mb-4 sm:h-16 sm:w-16">
            <svg
              className="h-6 w-6 text-gray-400 sm:h-8 sm:w-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-base font-medium text-gray-300 sm:text-lg">
            {t("agentsDistribution.empty.title")}
          </h3>
          <p className="text-sm text-gray-400">
            {t("agentsDistribution.empty.subtitle")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900 p-3 sm:p-6">
      <div className="mb-3 sm:mb-6">
        <h3 className="mb-1 text-base font-semibold text-white sm:mb-2 sm:text-lg">
          {t("agentsDistribution.title")}
        </h3>
        <p className="text-xs text-gray-400 sm:text-sm">
          {t("agentsDistribution.subtitle")}
        </p>
      </div>

      {/* Chart Container */}
      <div className="w-full">
        <div className="relative w-full" style={{ height: "250px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius="40%"
                outerRadius="70%"
                paddingAngle={0}
                dataKey="value"
                nameKey="name"
                startAngle={90}
                endAngle={450}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{ zIndex: 9999 }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Center text */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-white sm:text-3xl">
                {totalOrders}
              </div>
              <div className="mt-1 text-xs text-gray-400 sm:text-sm">
                {t("agentsDistribution.center.total")}
                <br className="sm:hidden" />{" "}
                {t("agentsDistribution.center.orders")}
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-2 w-full sm:mt-4">
          <CustomLegend payload={chartData} />
        </div>
      </div>
    </div>
  );
};

export default AiAgentsDistributionChart;
